// src/components/CanalsTracedTab.js - FIXED VERSION
import React, { useEffect, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    OutlinedInput, 
    Checkbox, 
    ListItemText, 
    TextField 
} from '@mui/material';
import { useFormContext } from '../context/FormContext';

// Constants from utilities.py
const CANAL_DESCRIPTIONS_MANDIBLE = {
    'Mandibular Canal – Right': 'Mandibular canal in the right posterior mandible, well defined.',
    'Mandibular Canal – Left': 'Mandibular canal in the left posterior mandible, well defined.',
    'Mental Foramen': 'Thin anterior extension from the mental foramen.',
    'Lingual Canal in Anterior Region': 'Thin lingual canal in the anterior region towards the lingual aspect of anterior mandible.'
};

const CANAL_DESCRIPTIONS_MAXILLA = {
    "PSA right": "Posterior superior alveolar canal in the right posterior maxilla in the lateral walls of maxillary sinuses parallel to the floor.",
    "PSA left": "Posterior superior alveolar canal in the left  posterior maxilla in the lateral walls of maxillary sinuses parallel to the floor.",
    "PSA right, left": "Posterior superior alveolar canal in the right and left posterior maxilla in the lateral walls of maxillary sinuses parallel to the floor.", 
    "Nasopalatine canal in the anterior region": "Nasopalatine canal in the anterior region towards the palatal aspect of 11, 21 region."
};

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const CanalsTracedTab = () => {
    const { state, actions } = useFormContext();
    
    // Get UI state
    const selectedCanals = state.canalsUIState.selectedCanals;
    const otherCanals = state.canalsUIState.otherCanals;

    console.log('CanalsTracedTab Rendered. Current State:', { 
        patientData: state.patientData, 
        reportType: state.reportType,
        selectedCanals,
        otherCanals
    });

    // FIXED: Memoize canal options to prevent recreating array on every render
    const canalOptions = useMemo(() => {
        return state.reportType === "Mandible - Basic Report" 
            ? Object.keys(CANAL_DESCRIPTIONS_MANDIBLE) 
            : Object.keys(CANAL_DESCRIPTIONS_MAXILLA);
    }, [state.reportType]);

    const handleCanalChange = (event) => {
        const { target: { value } } = event;
        const newSelectedCanals = typeof value === 'string' ? value.split(',') : value;
        actions.updateCanalsUIState({ selectedCanals: newSelectedCanals });
        console.log('CanalsTracedTab: Selected Canals changed:', newSelectedCanals);
    };

    const handleOtherCanalsChange = (event) => {
        actions.updateCanalsUIState({ otherCanals: event.target.value });
        console.log('CanalsTracedTab: Other Canals changed:', event.target.value);
    };

    // FIXED: Improved useEffect with proper dependencies and memoization
    useEffect(() => {
        const descriptions = state.reportType === "Mandible - Basic Report" 
            ? CANAL_DESCRIPTIONS_MANDIBLE 
            : CANAL_DESCRIPTIONS_MAXILLA;

        let combinedCanals = selectedCanals.map(canal => descriptions[canal]).filter(Boolean);

        if (otherCanals.trim()) {
            combinedCanals.push(otherCanals.trim());
        }
        
        const finalCanalsText = combinedCanals.join('\n');
        
        // FIXED: Only update if the value actually changed
        if (state.canalsTraced !== finalCanalsText) {
            actions.setCanalsTraced(finalCanalsText);
            console.log('CanalsTracedTab: Combined Canals for Context:', finalCanalsText);
        }
    }, [selectedCanals, otherCanals, state.reportType, state.canalsTraced, actions]);

    // FIXED: Only reset when explicitly needed, not on every report type change
    // Remove this problematic useEffect that was clearing selections
    // useEffect(() => {
    //     actions.updateCanalsUIState({ 
    //         selectedCanals: [], 
    //         otherCanals: '' 
    //     });
    // }, [state.reportType, actions]);

    // FIXED: Add a manual reset function instead
    const handleResetSelections = () => {
        actions.updateCanalsUIState({ 
            selectedCanals: [], 
            otherCanals: '' 
        });
    };

    if (!state.patientData) {
        return (
            <Box p={3}>
                <Typography variant="h6" color="textSecondary">
                    Please load patient data first from the sidebar.
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>
                Canals Traced
            </Typography>
            
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Report Type: {state.reportType}
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>
                    {state.reportType === "Mandible - Basic Report" 
                        ? "Mandibular Canals Traced" 
                        : "Maxillary Canals Traced"}
                </InputLabel>
                <Select
                    multiple
                    value={selectedCanals}
                    onChange={handleCanalChange}
                    input={
                        <OutlinedInput 
                            label={state.reportType === "Mandible - Basic Report" 
                                ? "Mandibular Canals Traced" 
                                : "Maxillary Canals Traced"} 
                        />
                    }
                    renderValue={(selected) => selected.join(', ')}
                    MenuProps={MenuProps}
                >
                    {canalOptions.map((name) => (
                        <MenuItem key={name} value={name}>
                            <Checkbox checked={selectedCanals.indexOf(name) > -1} />
                            <ListItemText primary={name} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            
            <TextField
                fullWidth
                label="If other canals are traced, please enter it here"
                multiline
                rows={3}
                value={otherCanals}
                onChange={handleOtherCanalsChange}
                placeholder="Enter any additional canal descriptions..."
                sx={{ mb: 2 }}
            />

            {/* FIXED: Add reset button */}
            {(selectedCanals.length > 0 || otherCanals.trim()) && (
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        onClick={handleResetSelections}
                        style={{
                            background: 'none',
                            border: '1px solid #ccc',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        Clear Selections
                    </button>
                </Box>
            )}

            {/* Preview of selected canals */}
            {(selectedCanals.length > 0 || otherCanals.trim()) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Preview of Canals Traced:
                    </Typography>
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                        {state.canalsTraced || 'No canals selected yet.'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default CanalsTracedTab;